/* =====================================================================
   SIMILAR PRODUCT RECOMMENDATION ENGINE — assets/similar-product.js
   =====================================================================
   v2 — Spec-first ranking: Processor + Price + Generation over Brand.

   WHAT DRIVES THE RECOMMENDATION (priority order):
     1. Product Type  — hard rule, never crossed (Laptop→Laptop,
                        Desktop→Desktop, Mini PC→Mini PC, Monitor→Monitor,
                        MacBook→MacBook).
     2. Processor     — hard preference: if ANY candidate shares the
                        current processor family (i5→i5), all other
                        families are dropped before scoring. Only when no
                        same-family product exists do other processors
                        become eligible as a fallback.
     3. Price         — hard ±10% band (outside it = excluded), then the
                        survivors are ranked by closeness (max 90 pts).
     4. Generation    — graduated by distance; the newer neighbour beats
                        the older one on ties (10th → 11th → 9th → 12th).
     5. Model / Display / Category / Persona / Tags — soft signals.
     6. Vendor        — a small +10 bonus only. Brand may tie-break two
                        otherwise-equal products; it can never outrank
                        processor, price or generation.

   DATA CONTRACT (from the Liquid snippet's #similar-product-json):
     `price` is expected in RUPEES (major units, e.g. 21399). If the
     snippet ever emits Shopify subunits/paise (e.g. 2139900), the
     engine detects the ~100x mismatch against the first batch of live
     candidates and auto-corrects (see reconcilePriceUnits), so the
     ±10% band and price scoring keep working either way.

   PERFORMANCE: unchanged. One /search/suggest.json request per fallback
   tier (same as before), nothing else. All filtering and scoring happens
   in memory on <=10 items per batch. No extra endpoints, no loops added.
   ===================================================================== */
(() => {
    "use strict";

    // Guard against the snippet/script executing twice on one page.
    if (window.similarProductEngineLoaded) return;
    window.similarProductEngineLoaded = true;

    /* =====================================================
       CONFIG
    ===================================================== */
    const CONFIG = {
        searchLimit: 10,          // Shopify max per request
        requestTimeoutMs: 2500,   // abort a stalled request and fall back faster
        priceFilterBandPercent: 10, // hard-exclude candidates further than this from current price
        // Tiered price score: [maxPercentDiff, points]. First matching tier wins.
        // Rescaled so price (max 90) is the strongest signal after processor.
        priceScoreTiers: [
            [2, 90],
            [4, 75],
            [6, 60],
            [8, 45],
            [10, 30]
            // anything beyond 10% is already excluded by priceFilterBandPercent above
        ],
        // Spec scoring table. Processor + Price must always outrank Vendor.
        scoring: {
            productType: 100,     // guaranteed equal by the hard filter; credited for spec parity
            processorFamily: 100, // MOST important signal (also enforced by preferMatchingProcessor)
            price: 90,            // SECOND most important — see priceScoreTiers / scorePrice()
            generation: 60,       // graduated by distance, newer wins ties — see scoreGeneration()
            modelFamily: 40,
            displaySize: 30,
            laptopCategory: 20,
            persona: 10,
            vendor: 10,           // small bonus only — brand must never override specs or budget
            tag: 5
        }
    };

    /* =====================================================
       CACHE DOM (read once, reused everywhere)
    ===================================================== */
    const jsonEl = document.getElementById("similar-product-json");
    const resultEl = document.getElementById("similar-product-result");

    if (!jsonEl || !resultEl) {
        console.error("[Similar Product] Required elements missing");
        return;
    }

    let currentProduct;
    try {
        currentProduct = JSON.parse(jsonEl.textContent);
    } catch (err) {
        console.error("[Similar Product] Invalid product JSON", err);
        return;
    }

    // Pre-compute the current product's normalized signals ONCE, instead
    // of re-deriving them for every candidate inside the scoring loop.
    const currentNormalized = {
        productType: normalizeProductType(currentProduct.type, currentProduct.title),
        vendor: normalizeText(currentProduct.vendor),
        processorFamily: extractProcessorFamily(currentProduct.processor_type || currentProduct.title),
        generation: extractGeneration(currentProduct.generation || currentProduct.gen || currentProduct.title),
        displaySize: extractDisplaySize(currentProduct.display_size || currentProduct.title),
        modelFamily: extractModelFamily(currentProduct.model || currentProduct.title),
        laptopCategory: normalizeText(currentProduct.laptop_category),
        persona: normalizeText(currentProduct.persona),
        tags: Array.isArray(currentProduct.tags) ? currentProduct.tags : [],
        generationNum: parseInt(extractGeneration(currentProduct.generation || currentProduct.gen || currentProduct.title), 10),
        price: Number(currentProduct.price)
    };

    const hasCurrentPrice = Number.isFinite(currentNormalized.price) && currentNormalized.price > 0;
    if (!hasCurrentPrice) {
        console.warn("[Similar Product] currentProduct.price missing/invalid — price filter & scoring skipped. See header comment in this file.");
    }

    // One-time flag for the paise/rupees sanity check (see reconcilePriceUnits).
    let priceUnitsChecked = false;

    // Paint the skeleton immediately — before any network call — so the
    // page never shows an empty gap while we search.
    renderSkeleton();

    const searchQueries = buildSearchQueries(currentProduct);

    // Single entry point. No duplicate fetches, no nested promise chains.
    findAndRenderSimilarProduct();

    /* =====================================================
       MAIN FLOW
    ===================================================== */
    async function findAndRenderSimilarProduct() {
        for (const query of searchQueries) {
            let rawProducts = [];

            try {
                rawProducts = await fetchProducts(query);
            } catch (err) {
                console.error("[Similar Product] Search failed:", query, err);
                continue; // try the next fallback query
            }

            if (!rawProducts.length) continue;

            // One-time guard: if the Liquid snippet emitted the current
            // price in paise, fix it before the ±10% band wipes out
            // every candidate. In-memory, runs once, costs nothing.
            reconcilePriceUnits(rawProducts);

            let candidates = filterCandidates(rawProducts);
            if (!candidates.length) continue;

            // HARD PREFERENCE (spec step 3): if this batch contains ANY
            // candidate on the same processor family, drop the rest.
            // This is what stops an i3 or i7 from ever winning over an
            // i5 when an i5 is available — regardless of brand, tags or
            // price closeness. Other families are only allowed back in
            // as a fallback when no same-family candidate exists.
            candidates = preferMatchingProcessor(candidates);

            const best = candidates
                .map(scoreCandidate)
                .sort((a, b) => b.score - a.score)[0];

            renderProduct(best);
            return;
        }

        renderEmptyState();
    }

    /* =====================================================
       SEARCH QUERY BUILDER — processor-led (was vendor-led).
       Priority per the new spec: Processor+Generation ->
       Processor -> Model -> Laptop Category -> Vendor -> Type.
       The candidate pool must be SPEC-similar across all
       brands, not brand-similar. Same architecture: tiered
       fallback queries against Predictive Search, first tier
       that yields a usable candidate wins.
    ===================================================== */
    function buildSearchQueries(product) {
        const vendor = product.vendor || "";
        const model = product.model || "";
        const laptopCategory = product.laptop_category || "";

        // Compact, cross-brand processor term ("i5", "ryzen 5") derived
        // from the metafield or, failing that, the product title. This
        // matches catalog titles like "Intel i5-8th Gen" far more
        // reliably than a raw metafield value such as "Intel Core i5".
        const processorTerm = processorSearchTerm(product.processor_type, product.title);
        const generationTerm = generationSearchTerm(product.generation || product.gen, product.title);
        const typeTerm = productTypeSearchTerm();

        const priorityTiers = [
            [processorTerm, generationTerm], // widest spec-first net, all brands
            [processorTerm],                 // same processor, any generation
            [model],                         // model-family fallback
            [laptopCategory],                // category fallback
            [vendor],                        // brand-only, near-last resort
            [typeTerm]                       // absolute last resort: anything of the same type
        ];

        const queries = priorityTiers
            .map(parts => parts.filter(Boolean).join(" ").trim())
            .filter(Boolean);

        // De-duplicate while preserving priority order.
        return [...new Set(queries)];
    }

    function processorSearchTerm(rawProcessor, title) {
        const family = extractProcessorFamily(rawProcessor || title);
        if (!family) return (rawProcessor || "").trim();
        if (family.indexOf("intel-") === 0) return family.slice(6); // "intel-i5" -> "i5"
        if (family.indexOf("ryzen-") === 0) return "ryzen " + family.slice(6);
        return family; // celeron, pentium, xeon, m1/m2/m3
    }

    function generationSearchTerm(rawGeneration, title) {
        const raw = (rawGeneration || "").trim();
        if (raw) return raw;

        const genNum = parseInt(extractGeneration(title), 10);
        if (!Number.isFinite(genNum)) return "";

        const suffix = genNum === 1 ? "st" : genNum === 2 ? "nd" : genNum === 3 ? "rd" : "th";
        return genNum + suffix + " Gen";
    }

    function productTypeSearchTerm() {
        switch (currentNormalized.productType) {
            case "laptop": return "Laptop";
            case "desktop": return "Desktop";
            case "mini-pc": return "Mini PC";
            case "monitor": return "Monitor";
            case "macbook": return "MacBook";
            case "all-in-one": return "All in One";
            default: return "";
        }
    }

    /* =====================================================
       SHOPIFY PREDICTIVE SEARCH
    ===================================================== */
    async function fetchProducts(query) {
        const url =
            `/search/suggest.json?q=${encodeURIComponent(query)}` +
            `&resources[type]=product` +
            `&resources[limit]=${CONFIG.searchLimit}` +
            `&resources[options][unavailable_products]=hide`; // ask Shopify to drop OOS server-side

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), CONFIG.requestTimeoutMs);

        try {
            const response = await fetch(url, {
                signal: controller.signal,
                priority: "high" // Fetch Priority API — no-op on unsupported browsers
            });

            if (!response.ok) return [];

            const data = await response.json();
            return data.resources?.results?.products || [];
        } finally {
            clearTimeout(timeout);
        }
    }

    /* =====================================================
       PRICE UNIT SANITY CHECK — one-time, in-memory.
       If the current price is ~100x the median candidate
       price, the Liquid snippet emitted paise. Convert once
       so the ±10% band keeps working instead of silently
       filtering out every candidate.
    ===================================================== */
    function reconcilePriceUnits(products) {
        if (priceUnitsChecked || !hasCurrentPrice) return;
        priceUnitsChecked = true;

        const prices = products
            .map(p => Number(p.price))
            .filter(p => Number.isFinite(p) && p > 0)
            .sort((a, b) => a - b);

        if (!prices.length) return;

        const median = prices[Math.floor(prices.length / 2)];
        const ratio = currentNormalized.price / median;

        // A genuine premium product is a few x the median at most; a
        // paise-vs-rupees mismatch lands around 100x. 20x–500x is safely
        // in "unit mismatch" territory.
        if (ratio >= 20 && ratio <= 500) {
            currentNormalized.price = currentNormalized.price / 100;
            console.warn("[Similar Product] currentProduct.price looked like paise — auto-converted to", currentNormalized.price);
        }
    }

    /* =====================================================
       FILTER — hard disqualifiers only. Anything that survives
       this can legitimately be scored and shown.
    ===================================================== */
    function filterCandidates(products) {
        return products.filter(product => {
            if (product.handle === currentProduct.handle) return false;

            // Defensive: unavailable_products=hide should already remove
            // these, but some catalogs report availability inconsistently.
            if (product.available === false) return false;

            // HARD RULE (spec): Laptop -> Laptop only, Desktop -> Desktop
            // only, Mini PC -> Mini PC only, Monitor -> Monitor only,
            // MacBook -> MacBook only. Never violated.
            const candidateType = normalizeProductType(product.type, product.title);
            if (candidateType !== currentNormalized.productType) return false;

            // Budget guardrail (spec step 4): never recommend anything
            // more than 10% above or below the current price. This stops
            // a technically-similar but commercially-irrelevant upsell
            // (e.g. Rs.19,999 laptop -> Rs.27,999+ laptop).
            if (hasCurrentPrice) {
                const candidatePrice = Number(product.price);
                if (Number.isFinite(candidatePrice)) {
                    const diffPercent = Math.abs(candidatePrice - currentNormalized.price) / currentNormalized.price * 100;
                    if (diffPercent > CONFIG.priceFilterBandPercent) return false;
                }
            }

            return true;
        });
    }

    /* =====================================================
       PROCESSOR PREFERENCE (spec step 3) — narrows to
       same-processor-family candidates when at least one
       exists; falls back to all families only when none do.
    ===================================================== */
    function preferMatchingProcessor(candidates) {
        if (!currentNormalized.processorFamily) return candidates;

        const sameProcessor = candidates.filter(
            product => extractProcessorFamily(product.title) === currentNormalized.processorFamily
        );

        // Only narrow if it doesn't leave us with nothing — an i7 is
        // still better than "no recommendation at all" when truly no
        // same-family product exists.
        return sameProcessor.length ? sameProcessor : candidates;
    }

    /* =====================================================
       SCORING ENGINE — spec priority:
       Processor 100 > Price 90 > Generation 60 > Model 40 >
       Display 30 > Category 20 > Persona 10 > Vendor 10 > Tag 5
    ===================================================== */
    function scoreCandidate(product) {
        const w = CONFIG.scoring;
        let score = 0;

        // Product type is already guaranteed equal by filterCandidates(),
        // but the point value is still credited here per the spec's table.
        score += w.productType;

        const candidateTags = Array.isArray(product.tags) ? product.tags : [];

        // 1) PROCESSOR — the single most important factor.
        const candidateProcessor = extractProcessorFamily(product.title);
        if (currentNormalized.processorFamily && candidateProcessor === currentNormalized.processorFamily) {
            score += w.processorFamily;
        }

        // 2) PRICE — budget similarity. Candidates outside the 10% band
        // never reach here (filterCandidates already removed them); this
        // ranks the survivors by how close they are within that band.
        score += scorePrice(product.price);

        // 3) GENERATION — graduated by distance: exact generation scores
        // full points, each step away scores less, and the NEWER
        // neighbour edges out the older one (10th -> 11th -> 9th -> 12th).
        score += scoreGeneration(product.title);

        // 4) Soft signals.
        const candidateModel = extractModelFamily(product.title);
        if (currentNormalized.modelFamily && candidateModel === currentNormalized.modelFamily) {
            score += w.modelFamily;
        }

        const candidateDisplay = extractDisplaySize(product.title);
        if (currentNormalized.displaySize && candidateDisplay === currentNormalized.displaySize) {
            score += w.displaySize;
        }

        // Laptop category / persona: Predictive Search has no metafields,
        // so this is a best-effort match against tags when the store
        // mirrors those metafield values as tags.
        if (currentNormalized.laptopCategory && candidateTags.some(t => normalizeText(t) === currentNormalized.laptopCategory)) {
            score += w.laptopCategory;
        }

        if (currentNormalized.persona && candidateTags.some(t => normalizeText(t) === currentNormalized.persona)) {
            score += w.persona;
        }

        // 5) VENDOR — deliberately tiny. Same brand is a nice-to-have
        // tie-breaker between near-identical machines; a Dell with the
        // same i5, generation and price MUST be able to beat an HP that
        // is further from the shopper's budget.
        if (currentNormalized.vendor && normalizeText(product.vendor) === currentNormalized.vendor) {
            score += w.vendor;
        }

        if (currentNormalized.tags.length && candidateTags.length) {
            currentNormalized.tags.forEach(tag => {
                if (candidateTags.includes(tag)) score += w.tag;
            });
        }

        return { ...product, score };
    }

    function scoreGeneration(candidateTitle) {
        const w = CONFIG.scoring.generation;
        const candidateGenNum = parseInt(extractGeneration(candidateTitle), 10);

        if (!Number.isFinite(currentNormalized.generationNum) || !Number.isFinite(candidateGenNum)) {
            return 0; // can't compare — no signal either way
        }

        const diff = candidateGenNum - currentNormalized.generationNum;
        const distance = Math.abs(diff);

        if (distance === 0) return w; // exact generation match — full points

        let points;
        if (distance === 1) points = w * 0.6;       // one generation off
        else if (distance === 2) points = w * 0.35; // two generations off
        else points = w * 0.1;                      // still same family, just far

        // Spec step 5 tie-break: prefer the newer neighbour at equal
        // distance (for a 10th-gen product: 11th > 9th, and 9th > 12th
        // because distance still dominates).
        if (diff > 0) points += 2;

        return Math.round(points);
    }

    function scorePrice(candidatePriceRaw) {
        if (!hasCurrentPrice) return 0;

        const candidatePrice = Number(candidatePriceRaw);
        if (!Number.isFinite(candidatePrice)) return 0;

        const diffPercent = Math.abs(candidatePrice - currentNormalized.price) / currentNormalized.price * 100;

        for (const [maxPercent, points] of CONFIG.priceScoreTiers) {
            if (diffPercent <= maxPercent) return points;
        }

        return 0; // beyond the last tier — shouldn't happen since priceFilterBandPercent already excludes >10%
    }

    /* =====================================================
       NORMALIZATION HELPERS (dependency-free, defensive)
    ===================================================== */
    function normalizeText(value) {
        return (value || "").toString().trim().toLowerCase();
    }

    function normalizeProductType(typeField, title) {
        const text = `${typeField || ""} ${title || ""}`.toLowerCase();

        if (/macbook/.test(text)) return "macbook";
        if (/mini[\s-]?pc/.test(text)) return "mini-pc";
        if (/all[\s-]?in[\s-]?one/.test(text)) return "all-in-one";
        if (/monitor/.test(text)) return "monitor";
        if (/desktop|tower|\bsff\b|micro\s*pc/.test(text)) return "desktop";
        if (/laptop|notebook|probook|elitebook|thinkpad|ideapad|latitude/.test(text)) return "laptop";

        return "other";
    }

    function extractProcessorFamily(text) {
        if (!text) return "";
        const t = text.toLowerCase();

        const intel = t.match(/\bi[3579]\b/);
        if (intel) return `intel-${intel[0]}`;

        const ryzen = t.match(/ryzen\s*([3579])/);
        if (ryzen) return `ryzen-${ryzen[1]}`;

        if (/celeron/.test(t)) return "celeron";
        if (/pentium/.test(t)) return "pentium";
        if (/\bxeon\b/.test(t)) return "xeon";

        const appleSilicon = t.match(/\bm[123]\b/);
        if (appleSilicon) return appleSilicon[0];

        return "";
    }

    function extractGeneration(text) {
        if (!text) return "";
        const t = text.toLowerCase();

        // "8th gen", "8th-gen", "11th Gen" — ordinal followed by "gen".
        let match = t.match(/(\d{1,2})(?:st|nd|rd|th)[\s-]*gen\b/);
        if (match) return match[1];

        // "i5-8th", "i7 8th" — catalog titles that drop the word "Gen"
        // (e.g. "ThinkPad L490 | Intel i5-8th | 14in HD").
        match = t.match(/\b(\d{1,2})(?:st|nd|rd|th)\b/);
        if (match) return match[1];

        // Last resort: "8 gen" without an ordinal suffix.
        match = t.match(/\b(\d{1,2})[\s-]*gen\b/);
        return match ? match[1] : "";
    }

    function extractDisplaySize(text) {
        if (!text) return "";
        const match = text.match(/(\d{1,2}(?:\.\d)?)\s*(?:"|\u201D|inch)/i);
        return match ? match[1] : "";
    }

    function extractModelFamily(text) {
        if (!text) return "";
        // Ignore OS version tokens ("Win 10 Pro", "Windows 11") so two
        // unrelated machines never look like the same model family.
        const cleaned = text.replace(/win(?:dows)?\s*1[01]\b/gi, "");
        // Grabs the leading alphanumeric model token, e.g. "440" from "440 G6".
        const match = cleaned.match(/\b([a-z]?\d{2,4})\b/i);
        return match ? match[1].toLowerCase() : "";
    }

    /* =====================================================
       RENDER — one DOM write per state, built off-DOM first
    ===================================================== */
    function renderSkeleton() {
        const skeleton = document.createElement("div");
        skeleton.className = "sp-skeleton";
        skeleton.innerHTML =
            `<div class="sp-skeleton-image"></div>` +
            `<div class="sp-skeleton-content">` +
            `<div class="line w90"></div>` +
            `<div class="line w70"></div>` +
            `<div class="line w40"></div>` +
            `</div>`;

        resultEl.replaceChildren(skeleton);
    }

    function renderProduct(product) {
        const image = product.featured_image?.url || product.image || "";
        const price = Number(product.price).toLocaleString("en-IN");

        const card = document.createElement("a");
        card.href = (product.url || "").split("?")[0];
        card.className = "sp-card"; // whole card is one clickable element

        const imageWrap = document.createElement("div");
        imageWrap.className = "sp-image";

        const img = document.createElement("img");
        img.src = image;
        img.alt = product.title;
        img.loading = "eager";
        img.decoding = "async";
        img.width = 95;
        img.height = 95;
        imageWrap.appendChild(img);

        const info = document.createElement("div");
        info.className = "sp-info";

        const badge = document.createElement("div");
        badge.className = "sp-badge";
        badge.textContent = "Recommended Alternative";

        const title = document.createElement("h4");
        title.textContent = product.title; // textContent avoids HTML-injection via title

        const priceEl = document.createElement("div");
        priceEl.className = "sp-price";
        priceEl.textContent = `\u20B9${price}`;

        const link = document.createElement("div");
        link.className = "sp-link";
        link.textContent = "View Product \u2192";

        info.append(badge, title, priceEl, link);
        card.append(imageWrap, info);

        resultEl.replaceChildren(card); // single DOM write, no layout shift vs skeleton

        console.log("[Similar Product] Recommended:", product.title, "| score:", product.score);
    }

    function renderEmptyState() {
        const empty = document.createElement("div");
        empty.className = "sp-empty";
        empty.textContent = "No Similar Product Available";

        resultEl.replaceChildren(empty);
    }

})();